import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { teacherActions } from "../slices/teacher";
import { TeacherMapper } from "../../mappers/TeacherMapper";
import { getErrorMessage } from "../../utils/errorUtils";
import { TeacherCreateRequestDto } from "../../shared/dtos/teacher/TeacherCreateRequestDto";
import { TeacherResponseDto } from "../../shared/dtos/teacher/TeacherResponseDto";
import { TeacherSummaryDto } from "../../shared/dtos/teacher/TeacherSummaryDto";
import { UpdateTeacherPayload } from "../../shared/interfaces/teacher/UpdateTeacherPayload";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";

export interface TeacherQueryParams extends PaginationQueryDto {
  branchId?: string;
}

export const createTeacher =
  (values: TeacherCreateRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(teacherActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<TeacherResponseDto>
      >("/teachers", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Teacher created successfully!");
        const listTeacherData = TeacherMapper.mapTeacherToSummary(data.data);
        dispatch(teacherActions.createSuccess(listTeacherData));
        dispatch(
          teacherActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(teacherActions.setErrorCreate(message));
    }
  };

export const getTeachers =
  (params?: TeacherQueryParams) => async (dispatch: AppDispatch) => {
    dispatch(teacherActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<TeacherSummaryDto>>
      >("/teachers", { headers: { requiresAuth: true }, params });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(teacherActions.setItems(data.data.content));
        dispatch(teacherActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(teacherActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getTeacherById = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(teacherActions.setLoadingFetchOne());
  try {
    const { data } = await axiosInstance.get<
      ApiResponseDto<TeacherResponseDto>
    >(`/teachers/${id}`, { headers: { requiresAuth: true } });

    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(teacherActions.setItem(data.data));
    }
  } catch (error) {
    dispatch(teacherActions.setErrorFetchOne(getErrorMessage(error)));
  }
};

export const updateTeacher =
  (payload: UpdateTeacherPayload) => async (dispatch: AppDispatch) => {
    dispatch(teacherActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<TeacherResponseDto>
      >(`/teachers/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Teacher updated successfully!");
        const listTeacherData = TeacherMapper.mapTeacherToSummary(data.data);
        dispatch(teacherActions.updateSuccess(listTeacherData));
        dispatch(
          teacherActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(teacherActions.setErrorUpdate(message));
    }
  };

export const deleteTeacher = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(teacherActions.setLoadingDelete());
  try {
    const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
      `/teachers/${id}`,
      { headers: { requiresAuth: true } },
    );

    if (data.status === ApiResponseStatus.SUCCESS) {
      toast.success(data.message || "Teacher deleted successfully!");
      dispatch(teacherActions.deleteSuccess(id));
      dispatch(
        teacherActions.setServerStatus({
          operation: "delete",
          status: data.statusCode || 200,
        }),
      );
    }
  } catch (error) {
    const message = getErrorMessage(error);
    toast.error(message);
    dispatch(teacherActions.setErrorDelete(message));
  }
};

export const resetTeacherServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(teacherActions.setServerStatus(null));
};
